/** i18n module Compétences RH (institution ?tab=rh-module&rh=competencies) — 11 langues. */

type RhCompetenciesStrings = Record<string, string>;

const fr: RhCompetenciesStrings = {
  'rh.competencies.title': 'Gestion des compétences',
  'rh.competencies.subtitle': 'Référentiel, cartographie et analyse des écarts',
  'rh.competencies.documentTitle': 'Compétences - LyrraStudio',
  'rh.competencies.tabs.framework': 'Référentiel',
  'rh.competencies.tabs.map': 'Cartographie',
  'rh.competencies.tabs.gap': 'Analyse des écarts',
  'rh.competencyFrameworkDefaults': 'Référentiel de compétences',
  'rh.competencies': 'compétences',
  'rh.searchCompetencies': 'Rechercher une compétence…',
  'rh.frameworkLoadForbidden': 'Vous ne pouvez pas consulter ce référentiel avec ce compte.',
  'rh.frameworkSession': 'Session expirée. Reconnectez-vous.',
  'rh.frameworkLoadError': 'Le référentiel ne peut pas être affiché pour le moment. Réessayez dans un instant.',
  'rh.frameworkSaveForbidden': 'Cette action est réservée aux responsables RH et aux administrateurs de l’organisation.',
  'rh.frameworkDuplicate': 'Ce nom est déjà utilisé. Choisissez un autre libellé.',
  'rh.frameworkSaveError': 'L’enregistrement n’a pas abouti. Réessayez ou vérifiez votre connexion.',
  'rh.frameworkReadOnly': 'Consultation seule — les modifications du référentiel sont effectuées par les responsables RH ou les administrateurs de l’organisation.',
  'rh.frameworkBuildSection': 'Construire le référentiel',
  'rh.frameworkNewDomainLabel': 'Nouveau domaine',
  'rh.frameworkNewDomainPlaceholder': 'Ex. : Savoir-faire métier',
  'rh.frameworkAddDomain': 'Ajouter le domaine',
  'rh.frameworkNewFamilyLabel': 'Nouvelle famille de compétences',
  'rh.frameworkParentDomain': 'Domaine parent',
  'rh.frameworkFamilyName': 'Nom de la famille',
  'rh.frameworkFamilyPlaceholder': 'Ex. : Communication',
  'rh.frameworkAddFamily': 'Ajouter',
  'rh.frameworkNewCompLabel': 'Nouvelle compétence',
  'rh.frameworkDomain': 'Domaine',
  'rh.frameworkFamily': 'Famille',
  'rh.frameworkCompName': 'Nom',
  'rh.frameworkCompPlaceholder': 'Ex. : Synthèse de documents',
  'rh.frameworkEchelle': 'Échelle',
  'rh.frameworkEchelleHint': 'Niveaux 1 à 5 (référence pour la grille d’échelle)',
  'rh.frameworkAddCompetency': 'Ajouter la compétence',
  'rh.frameworkDomainNameHint': 'Indiquez un nom de domaine (au moins 2 caractères).',
  'rh.frameworkDomainCreated': 'Domaine ajouté.',
  'rh.frameworkPickDomainFirst': 'Créez d’abord un domaine.',
  'rh.frameworkFamilyNameHint': 'Indiquez un nom de famille (au moins 2 caractères).',
  'rh.frameworkFamilyCreated': 'Famille ajoutée.',
  'rh.frameworkPickFamilyFirst': 'Choisissez une famille ou créez-en une dans un domaine.',
  'rh.frameworkCompNameHint': 'Indiquez un nom de compétence.',
  'rh.frameworkCompCreated': 'Compétence ajoutée.',
  'rh.frameworkDeleteSuccess': 'Élément supprimé du référentiel.',
  'rh.frameworkDeleteDomain': 'Supprimer ce domaine',
  'rh.frameworkDeleteFamily': 'Supprimer cette famille',
  'rh.frameworkDeleteCompetency': 'Supprimer cette compétence',
  'rh.frameworkDeleteTitle': 'Supprimer cet élément ?',
  'rh.frameworkDeleteMessage': 'Vous allez supprimer « {{name}} ». Cette action est définitive.',
  'rh.frameworkDeleteConfirm': 'Supprimer',
  'rh.competencyFrameworkEmpty': 'Encore aucun référentiel pour votre organisation.',
  'rh.competencyFrameworkEmptyUserHint':
    'Commencez par ajouter un domaine (grand thème), puis des familles et des compétences à l’aide du formulaire ci-dessus.',
  'rh.competencyFrameworkEmptyContactRh':
    'Demandez aux responsables RH ou à l’administrateur LyrraStudio de votre établissement de paramétrer votre référentiel.',
  'rh.skillShort': 'compétences',
  'rh.targetShort': 'cible',
  'rh.allDepartments': 'Tous les services',
  'rh.allCompetencies': 'Toutes les compétences',
  'rh.competencyMap': 'Cartographie',
  'rh.competencyMapEmpty':
    'Aucune évaluation validée avec un service renseigné : la carte sera remplie après les jugements RH sur les niveaux.',
  'rh.level': 'Niveau',
  'rh.department': 'Service',
  'rh.noValidatedSample': 'Aucune note',
  'rh.avgLevel': 'Moy.',
  'rh.departments': 'services',
  'rh.avgOverall': 'Moyenne globale',
  'rh.totalGaps': 'Écarts (négatifs ignorés)',
  'rh.criticalGaps': 'Critiques (écart ≥ 2)',
  'rh.avgGap': 'Écart moyen',
  'rh.gapAnalysis': 'Analyse des écarts',
  'rh.current': 'Actuel',
  'rh.required': 'Attendu',
  'rh.noGapsFilter': 'Aucun écart pour ce filtre.',
  'rh.noGapData': 'Aucun écart constaté (ou pas encore d’évaluations validées par rapport aux niveaux attendus).',
  'rh.priority.high': 'Critique',
  'rh.priority.medium': 'Modéré',
  'rh.priority.low': 'Faible',
  'rh.gapRecommendationSuffix': ' Formation ou plan de développement aligné sur le niveau attendu.',
};

const en: RhCompetenciesStrings = {
  'rh.competencies.title': 'Skills management',
  'rh.competencies.subtitle': 'Framework, heatmap and gap analysis',
  'rh.competencies.documentTitle': 'Skills - LyrraStudio',
  'rh.competencies.tabs.framework': 'Framework',
  'rh.competencies.tabs.map': 'Heatmap',
  'rh.competencies.tabs.gap': 'Gap analysis',
  'rh.competencyFrameworkDefaults': 'Skills framework',
  'rh.competencies': 'skills',
  'rh.searchCompetencies': 'Search a skill…',
  'rh.frameworkLoadForbidden': 'You cannot view this framework with this account.',
  'rh.frameworkSession': 'Session expired. Please sign in again.',
  'rh.frameworkLoadError': 'The framework cannot be displayed right now. Try again shortly.',
  'rh.frameworkSaveForbidden': 'This action is restricted to HR leads and organization administrators.',
  'rh.frameworkDuplicate': 'This name is already in use. Choose another label.',
  'rh.frameworkSaveError': 'Save failed. Try again or check your connection.',
  'rh.frameworkReadOnly':
    'Read-only — framework changes are made by HR leads or organization administrators.',
  'rh.frameworkBuildSection': 'Build the framework',
  'rh.frameworkNewDomainLabel': 'New domain',
  'rh.frameworkNewDomainPlaceholder': 'E.g. Job expertise',
  'rh.frameworkAddDomain': 'Add domain',
  'rh.frameworkNewFamilyLabel': 'New skill family',
  'rh.frameworkParentDomain': 'Parent domain',
  'rh.frameworkFamilyName': 'Family name',
  'rh.frameworkFamilyPlaceholder': 'E.g. Communication',
  'rh.frameworkAddFamily': 'Add',
  'rh.frameworkNewCompLabel': 'New skill',
  'rh.frameworkDomain': 'Domain',
  'rh.frameworkFamily': 'Family',
  'rh.frameworkCompName': 'Name',
  'rh.frameworkCompPlaceholder': 'E.g. Document synthesis',
  'rh.frameworkEchelle': 'Scale',
  'rh.frameworkEchelleHint': 'Levels 1 to 5 (reference for the rating grid)',
  'rh.frameworkAddCompetency': 'Add skill',
  'rh.frameworkDomainNameHint': 'Enter a domain name (at least 2 characters).',
  'rh.frameworkDomainCreated': 'Domain added.',
  'rh.frameworkPickDomainFirst': 'Create a domain first.',
  'rh.frameworkFamilyNameHint': 'Enter a family name (at least 2 characters).',
  'rh.frameworkFamilyCreated': 'Family added.',
  'rh.frameworkPickFamilyFirst': 'Select a family or create one in a domain.',
  'rh.frameworkCompNameHint': 'Enter a skill name.',
  'rh.frameworkCompCreated': 'Skill added.',
  'rh.frameworkDeleteSuccess': 'Item removed from the framework.',
  'rh.frameworkDeleteDomain': 'Delete this domain',
  'rh.frameworkDeleteFamily': 'Delete this family',
  'rh.frameworkDeleteCompetency': 'Delete this skill',
  'rh.frameworkDeleteTitle': 'Delete this item?',
  'rh.frameworkDeleteMessage': 'You are about to delete “{{name}}”. This cannot be undone.',
  'rh.frameworkDeleteConfirm': 'Delete',
  'rh.competencyFrameworkEmpty': 'No framework has been set up for your organization yet.',
  'rh.competencyFrameworkEmptyUserHint':
    'Start by adding a domain (broad theme), then families and skills using the form above.',
  'rh.competencyFrameworkEmptyContactRh':
    'Ask your HR leads or LyrraStudio administrator to configure your framework.',
  'rh.skillShort': 'skills',
  'rh.targetShort': 'target',
  'rh.allDepartments': 'All departments',
  'rh.allCompetencies': 'All skills',
  'rh.competencyMap': 'Heatmap',
  'rh.competencyMapEmpty':
    'No validated rating with a department on file: the map will populate after HR level assessments.',
  'rh.level': 'Level',
  'rh.department': 'Department',
  'rh.noValidatedSample': 'No rating',
  'rh.avgLevel': 'Avg.',
  'rh.departments': 'departments',
  'rh.avgOverall': 'Overall average',
  'rh.totalGaps': 'Gaps (negative ignored)',
  'rh.criticalGaps': 'Critical (gap ≥ 2)',
  'rh.avgGap': 'Average gap',
  'rh.gapAnalysis': 'Gap analysis',
  'rh.current': 'Current',
  'rh.required': 'Required',
  'rh.noGapsFilter': 'No gaps for this filter.',
  'rh.noGapData': 'No gaps found (or no validated assessments against expected levels yet).',
  'rh.priority.high': 'Critical',
  'rh.priority.medium': 'Moderate',
  'rh.priority.low': 'Low',
  'rh.gapRecommendationSuffix': ' Training or development plan aligned with the expected level.',
};

const es: RhCompetenciesStrings = {
  ...en,
  'rh.competencies.title': 'Gestión de competencias',
  'rh.competencies.subtitle': 'Referencial, cartografía y análisis de brechas',
  'rh.competencies.documentTitle': 'Competencias - LyrraStudio',
  'rh.competencies.tabs.framework': 'Referencial',
  'rh.competencies.tabs.map': 'Cartografía',
  'rh.competencies.tabs.gap': 'Análisis de brechas',
  'rh.competencyFrameworkDefaults': 'Referencial de competencias',
  'rh.competencies': 'competencias',
  'rh.searchCompetencies': 'Buscar una competencia…',
  'rh.frameworkLoadForbidden': 'No puede consultar este referencial con esta cuenta.',
  'rh.frameworkSession': 'Sesión caducada. Vuelva a iniciar sesión.',
  'rh.frameworkLoadError': 'El referencial no puede mostrarse ahora. Inténtelo de nuevo.',
  'rh.frameworkSaveForbidden': 'Acción reservada a responsables de RR. HH. y administradores.',
  'rh.frameworkDuplicate': 'Este nombre ya está en uso. Elija otro.',
  'rh.frameworkSaveError': 'No se pudo guardar. Inténtelo de nuevo.',
  'rh.frameworkReadOnly': 'Solo lectura — los cambios los realizan RR. HH. o administradores.',
  'rh.frameworkBuildSection': 'Construir el referencial',
  'rh.frameworkNewDomainLabel': 'Nuevo dominio',
  'rh.frameworkNewDomainPlaceholder': 'Ej.: Saber hacer profesional',
  'rh.frameworkAddDomain': 'Añadir dominio',
  'rh.frameworkNewFamilyLabel': 'Nueva familia de competencias',
  'rh.frameworkParentDomain': 'Dominio padre',
  'rh.frameworkFamilyName': 'Nombre de la familia',
  'rh.frameworkFamilyPlaceholder': 'Ej.: Comunicación',
  'rh.frameworkAddFamily': 'Añadir',
  'rh.frameworkNewCompLabel': 'Nueva competencia',
  'rh.frameworkDomain': 'Dominio',
  'rh.frameworkFamily': 'Familia',
  'rh.frameworkCompName': 'Nombre',
  'rh.frameworkCompPlaceholder': 'Ej.: Síntesis de documentos',
  'rh.frameworkEchelle': 'Escala',
  'rh.frameworkEchelleHint': 'Niveles 1 a 5 (referencia de la cuadrícula)',
  'rh.frameworkAddCompetency': 'Añadir competencia',
  'rh.frameworkDomainNameHint': 'Indique un nombre de dominio (mín. 2 caracteres).',
  'rh.frameworkDomainCreated': 'Dominio añadido.',
  'rh.frameworkPickDomainFirst': 'Cree primero un dominio.',
  'rh.frameworkFamilyNameHint': 'Indique un nombre de familia (mín. 2 caracteres).',
  'rh.frameworkFamilyCreated': 'Familia añadida.',
  'rh.frameworkPickFamilyFirst': 'Elija una familia o créela en un dominio.',
  'rh.frameworkCompNameHint': 'Indique un nombre de competencia.',
  'rh.frameworkCompCreated': 'Competencia añadida.',
  'rh.frameworkDeleteSuccess': 'Elemento eliminado del referencial.',
  'rh.frameworkDeleteDomain': 'Eliminar este dominio',
  'rh.frameworkDeleteFamily': 'Eliminar esta familia',
  'rh.frameworkDeleteCompetency': 'Eliminar esta competencia',
  'rh.frameworkDeleteTitle': '¿Eliminar este elemento?',
  'rh.frameworkDeleteMessage': 'Va a eliminar «{{name}}». Esta acción es definitiva.',
  'rh.frameworkDeleteConfirm': 'Eliminar',
  'rh.competencyFrameworkEmpty': 'Aún no hay referencial para su organización.',
  'rh.competencyFrameworkEmptyUserHint':
    'Empiece añadiendo un dominio, luego familias y competencias con el formulario superior.',
  'rh.competencyFrameworkEmptyContactRh':
    'Pida a RR. HH. o al administrador LyrraStudio que configure el referencial.',
  'rh.skillShort': 'competencias',
  'rh.targetShort': 'objetivo',
  'rh.allDepartments': 'Todos los servicios',
  'rh.allCompetencies': 'Todas las competencias',
  'rh.competencyMap': 'Cartografía',
  'rh.competencyMapEmpty':
    'Sin evaluación validada con servicio: el mapa se completará tras las valoraciones RR. HH.',
  'rh.level': 'Nivel',
  'rh.department': 'Servicio',
  'rh.noValidatedSample': 'Sin nota',
  'rh.avgLevel': 'Media',
  'rh.departments': 'servicios',
  'rh.avgOverall': 'Media global',
  'rh.totalGaps': 'Brechas (negativas ignoradas)',
  'rh.criticalGaps': 'Críticas (brecha ≥ 2)',
  'rh.avgGap': 'Brecha media',
  'rh.gapAnalysis': 'Análisis de brechas',
  'rh.current': 'Actual',
  'rh.required': 'Esperado',
  'rh.noGapsFilter': 'Ninguna brecha para este filtro.',
  'rh.noGapData': 'Sin brechas (o sin evaluaciones validadas frente a niveles esperados).',
  'rh.priority.high': 'Crítica',
  'rh.priority.medium': 'Moderada',
  'rh.priority.low': 'Baja',
  'rh.gapRecommendationSuffix': ' Formación o plan de desarrollo alineado con el nivel esperado.',
};

const de: RhCompetenciesStrings = {
  ...en,
  'rh.competencies.title': 'Kompetenzverwaltung',
  'rh.competencies.subtitle': 'Referenzrahmen, Heatmap und Gap-Analyse',
  'rh.competencies.documentTitle': 'Kompetenzen - LyrraStudio',
  'rh.competencies.tabs.framework': 'Referenzrahmen',
  'rh.competencies.tabs.map': 'Heatmap',
  'rh.competencies.tabs.gap': 'Gap-Analyse',
  'rh.competencyFrameworkDefaults': 'Kompetenzreferenzrahmen',
  'rh.competencies': 'Kompetenzen',
  'rh.searchCompetencies': 'Kompetenz suchen…',
  'rh.frameworkLoadForbidden': 'Mit diesem Konto können Sie den Referenzrahmen nicht einsehen.',
  'rh.frameworkSession': 'Sitzung abgelaufen. Bitte erneut anmelden.',
  'rh.frameworkLoadError': 'Der Referenzrahmen kann derzeit nicht angezeigt werden.',
  'rh.frameworkSaveForbidden': 'Aktion nur für HR-Verantwortliche und Administratoren.',
  'rh.frameworkDuplicate': 'Dieser Name wird bereits verwendet.',
  'rh.frameworkSaveError': 'Speichern fehlgeschlagen. Bitte erneut versuchen.',
  'rh.frameworkReadOnly': 'Nur Lesen — Änderungen durch HR oder Administratoren.',
  'rh.frameworkBuildSection': 'Referenzrahmen aufbauen',
  'rh.frameworkNewDomainLabel': 'Neue Domäne',
  'rh.frameworkNewDomainPlaceholder': 'z. B. Fachwissen',
  'rh.frameworkAddDomain': 'Domäne hinzufügen',
  'rh.frameworkNewFamilyLabel': 'Neue Kompetenzfamilie',
  'rh.frameworkParentDomain': 'Übergeordnete Domäne',
  'rh.frameworkFamilyName': 'Name der Familie',
  'rh.frameworkFamilyPlaceholder': 'z. B. Kommunikation',
  'rh.frameworkAddFamily': 'Hinzufügen',
  'rh.frameworkNewCompLabel': 'Neue Kompetenz',
  'rh.frameworkDomain': 'Domäne',
  'rh.frameworkFamily': 'Familie',
  'rh.frameworkCompName': 'Name',
  'rh.frameworkCompPlaceholder': 'z. B. Dokumentensynthese',
  'rh.frameworkEchelle': 'Skala',
  'rh.frameworkEchelleHint': 'Stufen 1 bis 5 (Referenz für die Bewertungsmatrix)',
  'rh.frameworkAddCompetency': 'Kompetenz hinzufügen',
  'rh.frameworkDomainNameHint': 'Domänenname (mind. 2 Zeichen).',
  'rh.frameworkDomainCreated': 'Domäne hinzugefügt.',
  'rh.frameworkPickDomainFirst': 'Zuerst eine Domäne anlegen.',
  'rh.frameworkFamilyNameHint': 'Familienname (mind. 2 Zeichen).',
  'rh.frameworkFamilyCreated': 'Familie hinzugefügt.',
  'rh.frameworkPickFamilyFirst': 'Familie wählen oder in einer Domäne anlegen.',
  'rh.frameworkCompNameHint': 'Kompetenzname angeben.',
  'rh.frameworkCompCreated': 'Kompetenz hinzugefügt.',
  'rh.frameworkDeleteSuccess': 'Element aus dem Referenzrahmen entfernt.',
  'rh.frameworkDeleteDomain': 'Diese Domäne löschen',
  'rh.frameworkDeleteFamily': 'Diese Familie löschen',
  'rh.frameworkDeleteCompetency': 'Diese Kompetenz löschen',
  'rh.frameworkDeleteTitle': 'Dieses Element löschen?',
  'rh.frameworkDeleteMessage': 'Sie löschen «{{name}}». Dies ist endgültig.',
  'rh.frameworkDeleteConfirm': 'Löschen',
  'rh.competencyFrameworkEmpty': 'Noch kein Referenzrahmen für Ihre Organisation.',
  'rh.competencyFrameworkEmptyUserHint':
    'Beginnen Sie mit einer Domäne, dann Familien und Kompetenzen im Formular oben.',
  'rh.competencyFrameworkEmptyContactRh':
    'Bitten Sie HR oder den LyrraStudio-Administrator um die Einrichtung.',
  'rh.skillShort': 'Kompetenzen',
  'rh.targetShort': 'Ziel',
  'rh.allDepartments': 'Alle Abteilungen',
  'rh.allCompetencies': 'Alle Kompetenzen',
  'rh.competencyMap': 'Heatmap',
  'rh.competencyMapEmpty':
    'Keine validierte Bewertung mit Abteilung — die Karte füllt sich nach HR-Bewertungen.',
  'rh.level': 'Stufe',
  'rh.department': 'Abteilung',
  'rh.noValidatedSample': 'Keine Note',
  'rh.avgLevel': 'Ø',
  'rh.departments': 'Abteilungen',
  'rh.avgOverall': 'Gesamtdurchschnitt',
  'rh.totalGaps': 'Lücken (negative ignoriert)',
  'rh.criticalGaps': 'Kritisch (Lücke ≥ 2)',
  'rh.avgGap': 'Durchschnittliche Lücke',
  'rh.gapAnalysis': 'Gap-Analyse',
  'rh.current': 'Ist',
  'rh.required': 'Soll',
  'rh.noGapsFilter': 'Keine Lücken für diesen Filter.',
  'rh.noGapData': 'Keine Lücken (oder noch keine validierten Bewertungen).',
  'rh.priority.high': 'Kritisch',
  'rh.priority.medium': 'Mittel',
  'rh.priority.low': 'Niedrig',
  'rh.gapRecommendationSuffix': ' Schulung oder Entwicklungsplan passend zum Sollniveau.',
};

const it: RhCompetenciesStrings = {
  ...en,
  'rh.competencies.title': 'Gestione competenze',
  'rh.competencies.subtitle': 'Referenziale, mappa e analisi gap',
  'rh.competencies.documentTitle': 'Competenze - LyrraStudio',
  'rh.competencies.tabs.framework': 'Referenziale',
  'rh.competencies.tabs.map': 'Mappa',
  'rh.competencies.tabs.gap': 'Analisi gap',
  'rh.competencyFrameworkDefaults': 'Referenziale competenze',
  'rh.competencies': 'competenze',
  'rh.searchCompetencies': 'Cerca una competenza…',
  'rh.frameworkLoadForbidden': 'Non puoi consultare questo referenziale con questo account.',
  'rh.frameworkSession': 'Sessione scaduta. Accedi di nuovo.',
  'rh.frameworkLoadError': 'Il referenziale non può essere mostrato al momento.',
  'rh.frameworkSaveForbidden': 'Azione riservata a HR e amministratori.',
  'rh.frameworkDuplicate': 'Nome già in uso. Scegline un altro.',
  'rh.frameworkSaveError': 'Salvataggio non riuscito. Riprova.',
  'rh.frameworkReadOnly': 'Sola lettura — modifiche da HR o amministratori.',
  'rh.frameworkBuildSection': 'Costruire il referenziale',
  'rh.frameworkNewDomainLabel': 'Nuovo dominio',
  'rh.frameworkNewDomainPlaceholder': 'Es.: Competenze professionali',
  'rh.frameworkAddDomain': 'Aggiungi dominio',
  'rh.frameworkNewFamilyLabel': 'Nuova famiglia di competenze',
  'rh.frameworkParentDomain': 'Dominio padre',
  'rh.frameworkFamilyName': 'Nome famiglia',
  'rh.frameworkFamilyPlaceholder': 'Es.: Comunicazione',
  'rh.frameworkAddFamily': 'Aggiungi',
  'rh.frameworkNewCompLabel': 'Nuova competenza',
  'rh.frameworkDomain': 'Dominio',
  'rh.frameworkFamily': 'Famiglia',
  'rh.frameworkCompName': 'Nome',
  'rh.frameworkCompPlaceholder': 'Es.: Sintesi documenti',
  'rh.frameworkEchelle': 'Scala',
  'rh.frameworkEchelleHint': 'Livelli da 1 a 5 (riferimento griglia)',
  'rh.frameworkAddCompetency': 'Aggiungi competenza',
  'rh.frameworkDomainNameHint': 'Nome dominio (min. 2 caratteri).',
  'rh.frameworkDomainCreated': 'Dominio aggiunto.',
  'rh.frameworkPickDomainFirst': 'Crea prima un dominio.',
  'rh.frameworkFamilyNameHint': 'Nome famiglia (min. 2 caratteri).',
  'rh.frameworkFamilyCreated': 'Famiglia aggiunta.',
  'rh.frameworkPickFamilyFirst': 'Scegli una famiglia o creane una in un dominio.',
  'rh.frameworkCompNameHint': 'Indica il nome della competenza.',
  'rh.frameworkCompCreated': 'Competenza aggiunta.',
  'rh.frameworkDeleteSuccess': 'Elemento rimosso dal referenziale.',
  'rh.frameworkDeleteDomain': 'Elimina questo dominio',
  'rh.frameworkDeleteFamily': 'Elimina questa famiglia',
  'rh.frameworkDeleteCompetency': 'Elimina questa competenza',
  'rh.frameworkDeleteTitle': 'Eliminare questo elemento?',
  'rh.frameworkDeleteMessage': 'Stai per eliminare «{{name}}». Azione definitiva.',
  'rh.frameworkDeleteConfirm': 'Elimina',
  'rh.competencyFrameworkEmpty': 'Nessun referenziale per la tua organizzazione.',
  'rh.competencyFrameworkEmptyUserHint':
    'Inizia con un dominio, poi famiglie e competenze con il modulo sopra.',
  'rh.competencyFrameworkEmptyContactRh':
    'Chiedi a HR o all’amministratore LyrraStudio di configurare il referenziale.',
  'rh.skillShort': 'competenze',
  'rh.targetShort': 'obiettivo',
  'rh.allDepartments': 'Tutti i servizi',
  'rh.allCompetencies': 'Tutte le competenze',
  'rh.competencyMap': 'Mappa',
  'rh.competencyMapEmpty':
    'Nessuna valutazione validata con servizio: la mappa si popolerà dopo le valutazioni HR.',
  'rh.level': 'Livello',
  'rh.department': 'Servizio',
  'rh.noValidatedSample': 'Nessuna nota',
  'rh.avgLevel': 'Media',
  'rh.departments': 'servizi',
  'rh.avgOverall': 'Media globale',
  'rh.totalGaps': 'Gap (negativi ignorati)',
  'rh.criticalGaps': 'Critici (gap ≥ 2)',
  'rh.avgGap': 'Gap medio',
  'rh.gapAnalysis': 'Analisi gap',
  'rh.current': 'Attuale',
  'rh.required': 'Atteso',
  'rh.noGapsFilter': 'Nessun gap per questo filtro.',
  'rh.noGapData': 'Nessun gap (o valutazioni non ancora validate).',
  'rh.priority.high': 'Critico',
  'rh.priority.medium': 'Moderato',
  'rh.priority.low': 'Basso',
  'rh.gapRecommendationSuffix': ' Formazione o piano di sviluppo allineato al livello atteso.',
};

const pt: RhCompetenciesStrings = {
  ...en,
  'rh.competencies.title': 'Gestão de competências',
  'rh.competencies.subtitle': 'Referencial, mapa e análise de lacunas',
  'rh.competencies.documentTitle': 'Competências - LyrraStudio',
  'rh.competencies.tabs.framework': 'Referencial',
  'rh.competencies.tabs.map': 'Mapa',
  'rh.competencies.tabs.gap': 'Análise de lacunas',
  'rh.competencyFrameworkDefaults': 'Referencial de competências',
  'rh.competencies': 'competências',
  'rh.searchCompetencies': 'Pesquisar competência…',
  'rh.frameworkLoadForbidden': 'Não pode consultar este referencial com esta conta.',
  'rh.frameworkSession': 'Sessão expirada. Inicie sessão novamente.',
  'rh.frameworkLoadError': 'O referencial não pode ser exibido de momento.',
  'rh.frameworkSaveForbidden': 'Ação reservada a RH e administradores.',
  'rh.frameworkDuplicate': 'Este nome já está em uso.',
  'rh.frameworkSaveError': 'Falha ao guardar. Tente novamente.',
  'rh.frameworkReadOnly': 'Somente leitura — alterações por RH ou administradores.',
  'rh.frameworkBuildSection': 'Construir o referencial',
  'rh.frameworkNewDomainLabel': 'Novo domínio',
  'rh.frameworkNewDomainPlaceholder': 'Ex.: Saber-fazer profissional',
  'rh.frameworkAddDomain': 'Adicionar domínio',
  'rh.frameworkNewFamilyLabel': 'Nova família de competências',
  'rh.frameworkParentDomain': 'Domínio pai',
  'rh.frameworkFamilyName': 'Nome da família',
  'rh.frameworkFamilyPlaceholder': 'Ex.: Comunicação',
  'rh.frameworkAddFamily': 'Adicionar',
  'rh.frameworkNewCompLabel': 'Nova competência',
  'rh.frameworkDomain': 'Domínio',
  'rh.frameworkFamily': 'Família',
  'rh.frameworkCompName': 'Nome',
  'rh.frameworkCompPlaceholder': 'Ex.: Síntese de documentos',
  'rh.frameworkEchelle': 'Escala',
  'rh.frameworkEchelleHint': 'Níveis 1 a 5 (referência da grelha)',
  'rh.frameworkAddCompetency': 'Adicionar competência',
  'rh.frameworkDomainNameHint': 'Nome do domínio (mín. 2 caracteres).',
  'rh.frameworkDomainCreated': 'Domínio adicionado.',
  'rh.frameworkPickDomainFirst': 'Crie primeiro um domínio.',
  'rh.frameworkFamilyNameHint': 'Nome da família (mín. 2 caracteres).',
  'rh.frameworkFamilyCreated': 'Família adicionada.',
  'rh.frameworkPickFamilyFirst': 'Escolha uma família ou crie uma num domínio.',
  'rh.frameworkCompNameHint': 'Indique o nome da competência.',
  'rh.frameworkCompCreated': 'Competência adicionada.',
  'rh.frameworkDeleteSuccess': 'Elemento removido do referencial.',
  'rh.frameworkDeleteDomain': 'Eliminar este domínio',
  'rh.frameworkDeleteFamily': 'Eliminar esta família',
  'rh.frameworkDeleteCompetency': 'Eliminar esta competência',
  'rh.frameworkDeleteTitle': 'Eliminar este elemento?',
  'rh.frameworkDeleteMessage': 'Vai eliminar «{{name}}». Ação definitiva.',
  'rh.frameworkDeleteConfirm': 'Eliminar',
  'rh.competencyFrameworkEmpty': 'Ainda sem referencial para a sua organização.',
  'rh.competencyFrameworkEmptyUserHint':
    'Comece por um domínio, depois famílias e competências no formulário acima.',
  'rh.competencyFrameworkEmptyContactRh':
    'Peça ao RH ou administrador LyrraStudio para configurar o referencial.',
  'rh.skillShort': 'competências',
  'rh.targetShort': 'alvo',
  'rh.allDepartments': 'Todos os serviços',
  'rh.allCompetencies': 'Todas as competências',
  'rh.competencyMap': 'Mapa',
  'rh.competencyMapEmpty':
    'Sem avaliação validada com serviço: o mapa será preenchido após avaliações RH.',
  'rh.level': 'Nível',
  'rh.department': 'Serviço',
  'rh.noValidatedSample': 'Sem nota',
  'rh.avgLevel': 'Méd.',
  'rh.departments': 'serviços',
  'rh.avgOverall': 'Média global',
  'rh.totalGaps': 'Lacunas (negativas ignoradas)',
  'rh.criticalGaps': 'Críticas (lacuna ≥ 2)',
  'rh.avgGap': 'Lacuna média',
  'rh.gapAnalysis': 'Análise de lacunas',
  'rh.current': 'Atual',
  'rh.required': 'Esperado',
  'rh.noGapsFilter': 'Nenhuma lacuna para este filtro.',
  'rh.noGapData': 'Sem lacunas (ou avaliações ainda não validadas).',
  'rh.priority.high': 'Crítica',
  'rh.priority.medium': 'Moderada',
  'rh.priority.low': 'Baixa',
  'rh.gapRecommendationSuffix': ' Formação ou plano de desenvolvimento alinhado ao nível esperado.',
};

const lb: RhCompetenciesStrings = {
  ...fr,
  'rh.competencies.title': 'Kompetenze-Verwaltung',
  'rh.competencies.subtitle': 'Referenzial, Kaart an Ecart-Analys',
  'rh.competencies.documentTitle': 'Kompetenzen - LyrraStudio',
  'rh.competencies.tabs.framework': 'Referenzial',
  'rh.competencies.tabs.map': 'Kaartographie',
  'rh.competencies.tabs.gap': 'Ecart-Analys',
  'rh.competencyFrameworkDefaults': 'Kompetenze-Referenzial',
  'rh.competencies': 'Kompetenzen',
  'rh.searchCompetencies': 'Eng Kompetenz sichen…',
  'rh.frameworkLoadForbidden': 'Dir kënnt dëse Referenzial mat dësem Kont net consultéieren.',
  'rh.frameworkSession': 'Sessioun ofgelaf. Mellt Iech nei un.',
  'rh.frameworkLoadError': 'De Referenzial kann elo net ugewise ginn.',
  'rh.frameworkSaveForbidden': 'Aktioun nëmme fir HR-Verantwortlech an Administrateuren.',
  'rh.frameworkDuplicate': 'Dësen Numm gëtt schonn benotzt.',
  'rh.frameworkSaveError': 'Späichere feelgeschloen. Probéiert nach eng Kéier.',
  'rh.frameworkReadOnly': 'Nëmme Liesen — Ännerungen duerch HR oder Administrateuren.',
  'rh.frameworkBuildSection': 'De Referenzial opbauen',
  'rh.frameworkNewDomainLabel': 'Neien Domain',
  'rh.frameworkNewDomainPlaceholder': 'z. B. Beruffskompetenzen',
  'rh.frameworkAddDomain': 'Domain derbäisetzen',
  'rh.frameworkNewFamilyLabel': 'Nei Kompetenz-Famill',
  'rh.frameworkParentDomain': 'Iwwergeuerdenten Domain',
  'rh.frameworkFamilyName': 'Numm vun der Famill',
  'rh.frameworkFamilyPlaceholder': 'z. B. Kommunikatioun',
  'rh.frameworkAddFamily': 'Derbäisetzen',
  'rh.frameworkNewCompLabel': 'Nei Kompetenz',
  'rh.frameworkDomain': 'Domain',
  'rh.frameworkFamily': 'Famill',
  'rh.frameworkCompName': 'Numm',
  'rh.frameworkCompPlaceholder': 'z. B. Dokumentesynthese',
  'rh.frameworkEchelle': 'Skala',
  'rh.frameworkEchelleHint': 'Niveauen 1 bis 5',
  'rh.frameworkAddCompetency': 'Kompetenz derbäisetzen',
  'rh.frameworkDomainNameHint': 'Domainnumm (min. 2 Zeechen).',
  'rh.frameworkDomainCreated': 'Domain derbäigesat.',
  'rh.frameworkPickDomainFirst': 'Erst e Domain uleeën.',
  'rh.frameworkFamilyNameHint': 'Familljennumm (min. 2 Zeechen).',
  'rh.frameworkFamilyCreated': 'Famill derbäigesat.',
  'rh.frameworkPickFamilyFirst': 'Wielt eng Famill oder leet eng an engem Domain un.',
  'rh.frameworkCompNameHint': 'Gitt de Kompetenznumm un.',
  'rh.frameworkCompCreated': 'Kompetenz derbäigesat.',
  'rh.frameworkDeleteSuccess': 'Element aus dem Referenzial geläscht.',
  'rh.frameworkDeleteDomain': 'Dësen Domain läschen',
  'rh.frameworkDeleteFamily': 'Dës Famill läschen',
  'rh.frameworkDeleteCompetency': 'Dës Kompetenz läschen',
  'rh.frameworkDeleteTitle': 'Dëst Element läschen?',
  'rh.frameworkDeleteMessage': 'Dir läscht «{{name}}». Dëst ass definitiv.',
  'rh.frameworkDeleteConfirm': 'Läschen',
  'rh.competencyFrameworkEmpty': 'Nach kee Referenzial fir Är Organisatioun.',
  'rh.competencyFrameworkEmptyUserHint':
    'Fänkt mat engem Domain un, dann Famillen a Kompetenzen am Formulaire uewen.',
  'rh.competencyFrameworkEmptyContactRh':
    'Frot Är HR oder de LyrraStudio-Administrateur fir d’Ariichtung.',
  'rh.skillShort': 'Kompetenzen',
  'rh.targetShort': 'Zil',
  'rh.allDepartments': 'All Servicer',
  'rh.allCompetencies': 'All Kompetenzen',
  'rh.competencyMap': 'Kaartographie',
  'rh.competencyMapEmpty':
    'Keng validéiert Bewäertung mat Service — d’Kaart fëllt sech no den HR-Bewäertungen.',
  'rh.level': 'Niveau',
  'rh.department': 'Service',
  'rh.noValidatedSample': 'Keng Note',
  'rh.avgLevel': 'Moy.',
  'rh.departments': 'Servicer',
  'rh.avgOverall': 'Global Moyenne',
  'rh.totalGaps': 'Ecarten (negativ ignoréiert)',
  'rh.criticalGaps': 'Kritesch (Ecart ≥ 2)',
  'rh.avgGap': 'Duerchschnëttlechen Ecart',
  'rh.gapAnalysis': 'Ecart-Analys',
  'rh.current': 'Aktuell',
  'rh.required': 'Erwart',
  'rh.noGapsFilter': 'Keen Ecart fir dëse Filter.',
  'rh.noGapData': 'Keng Ecarten (oder nach keng validéiert Bewäertungen).',
  'rh.priority.high': 'Kritesch',
  'rh.priority.medium': 'Mëttel',
  'rh.priority.low': 'Niddreg',
  'rh.gapRecommendationSuffix': ' Formatioun oder Entwécklungsplang no dem erwaarten Niveau.',
};

const uk: RhCompetenciesStrings = {
  ...en,
  'rh.competencies.title': 'Управління компетенціями',
  'rh.competencies.subtitle': 'Довідник, карта та аналіз розривів',
  'rh.competencies.documentTitle': 'Компетенції - LyrraStudio',
  'rh.competencies.tabs.framework': 'Довідник',
  'rh.competencies.tabs.map': 'Карта',
  'rh.competencies.tabs.gap': 'Аналіз розривів',
  'rh.competencyFrameworkDefaults': 'Довідник компетенцій',
  'rh.competencies': 'компетенції',
  'rh.searchCompetencies': 'Пошук компетенції…',
  'rh.frameworkLoadForbidden': 'Ви не можете переглянути цей довідник з цим обліковим записом.',
  'rh.frameworkSession': 'Сесію завершено. Увійдіть знову.',
  'rh.frameworkLoadError': 'Довідник зараз не відображається. Спробуйте пізніше.',
  'rh.frameworkSaveForbidden': 'Дія лише для HR та адміністраторів.',
  'rh.frameworkDuplicate': 'Ця назва вже використовується.',
  'rh.frameworkSaveError': 'Не вдалося зберегти. Спробуйте знову.',
  'rh.frameworkReadOnly': 'Лише перегляд — зміни роблять HR або адміністратори.',
  'rh.frameworkBuildSection': 'Побудувати довідник',
  'rh.frameworkNewDomainLabel': 'Нова доменна область',
  'rh.frameworkNewDomainPlaceholder': 'Напр.: Професійні навички',
  'rh.frameworkAddDomain': 'Додати домен',
  'rh.frameworkNewFamilyLabel': 'Нова сім’я компетенцій',
  'rh.frameworkParentDomain': 'Батьківський домен',
  'rh.frameworkFamilyName': 'Назва сім’ї',
  'rh.frameworkFamilyPlaceholder': 'Напр.: Комунікація',
  'rh.frameworkAddFamily': 'Додати',
  'rh.frameworkNewCompLabel': 'Нова компетенція',
  'rh.frameworkDomain': 'Домен',
  'rh.frameworkFamily': 'Сім’я',
  'rh.frameworkCompName': 'Назва',
  'rh.frameworkCompPlaceholder': 'Напр.: Синтез документів',
  'rh.frameworkEchelle': 'Шкала',
  'rh.frameworkEchelleHint': 'Рівні 1–5 (для сітки оцінювання)',
  'rh.frameworkAddCompetency': 'Додати компетенцію',
  'rh.frameworkDomainNameHint': 'Назва домену (мін. 2 символи).',
  'rh.frameworkDomainCreated': 'Домен додано.',
  'rh.frameworkPickDomainFirst': 'Спочатку створіть домен.',
  'rh.frameworkFamilyNameHint': 'Назва сім’ї (мін. 2 символи).',
  'rh.frameworkFamilyCreated': 'Сім’ю додано.',
  'rh.frameworkPickFamilyFirst': 'Оберіть сім’ю або створіть у домені.',
  'rh.frameworkCompNameHint': 'Вкажіть назву компетенції.',
  'rh.frameworkCompCreated': 'Компетенцію додано.',
  'rh.frameworkDeleteSuccess': 'Елемент видалено з довідника.',
  'rh.frameworkDeleteDomain': 'Видалити цей домен',
  'rh.frameworkDeleteFamily': 'Видалити цю сім’ю',
  'rh.frameworkDeleteCompetency': 'Видалити цю компетенцію',
  'rh.frameworkDeleteTitle': 'Видалити цей елемент?',
  'rh.frameworkDeleteMessage': 'Ви видалите «{{name}}». Це незворотно.',
  'rh.frameworkDeleteConfirm': 'Видалити',
  'rh.competencyFrameworkEmpty': 'Довідник для вашої організації ще не налаштовано.',
  'rh.competencyFrameworkEmptyUserHint':
    'Почніть з домену, потім додайте сім’ї та компетенції у формі вище.',
  'rh.competencyFrameworkEmptyContactRh':
    'Зверніться до HR або адміністратора LyrraStudio для налаштування.',
  'rh.skillShort': 'компетенції',
  'rh.targetShort': 'ціль',
  'rh.allDepartments': 'Усі підрозділи',
  'rh.allCompetencies': 'Усі компетенції',
  'rh.competencyMap': 'Карта',
  'rh.competencyMapEmpty':
    'Немає підтверджених оцінок із підрозділом — карта заповниться після оцінок HR.',
  'rh.level': 'Рівень',
  'rh.department': 'Підрозділ',
  'rh.noValidatedSample': 'Немає оцінки',
  'rh.avgLevel': 'Сер.',
  'rh.departments': 'підрозділи',
  'rh.avgOverall': 'Загальний середній',
  'rh.totalGaps': 'Розриви (негативні ігноруються)',
  'rh.criticalGaps': 'Критичні (розрив ≥ 2)',
  'rh.avgGap': 'Середній розрив',
  'rh.gapAnalysis': 'Аналіз розривів',
  'rh.current': 'Поточний',
  'rh.required': 'Очікуваний',
  'rh.noGapsFilter': 'Немає розривів для цього фільтра.',
  'rh.noGapData': 'Розривів не виявлено (або ще немає підтверджених оцінок).',
  'rh.priority.high': 'Критичний',
  'rh.priority.medium': 'Помірний',
  'rh.priority.low': 'Низький',
  'rh.gapRecommendationSuffix': ' Навчання або план розвитку відповідно до очікуваного рівня.',
};

const ru: RhCompetenciesStrings = {
  ...en,
  'rh.competencies.title': 'Управление компетенциями',
  'rh.competencies.subtitle': 'Справочник, карта и анализ разрывов',
  'rh.competencies.documentTitle': 'Компетенции - LyrraStudio',
  'rh.competencies.tabs.framework': 'Справочник',
  'rh.competencies.tabs.map': 'Карта',
  'rh.competencies.tabs.gap': 'Анализ разрывов',
  'rh.competencyFrameworkDefaults': 'Справочник компетенций',
  'rh.competencies': 'компетенции',
  'rh.searchCompetencies': 'Поиск компетенции…',
  'rh.frameworkLoadForbidden': 'Вы не можете просмотреть этот справочник с данной учётной записью.',
  'rh.frameworkSession': 'Сеанс истёк. Войдите снова.',
  'rh.frameworkLoadError': 'Справочник сейчас недоступен. Повторите попытку.',
  'rh.frameworkSaveForbidden': 'Действие доступно HR и администраторам.',
  'rh.frameworkDuplicate': 'Это имя уже используется.',
  'rh.frameworkSaveError': 'Не удалось сохранить. Повторите попытку.',
  'rh.frameworkReadOnly': 'Только просмотр — изменения вносят HR или администраторы.',
  'rh.frameworkBuildSection': 'Построить справочник',
  'rh.frameworkNewDomainLabel': 'Новый домен',
  'rh.frameworkNewDomainPlaceholder': 'Напр.: Профессиональные навыки',
  'rh.frameworkAddDomain': 'Добавить домен',
  'rh.frameworkNewFamilyLabel': 'Новое семейство компетенций',
  'rh.frameworkParentDomain': 'Родительский домен',
  'rh.frameworkFamilyName': 'Название семейства',
  'rh.frameworkFamilyPlaceholder': 'Напр.: Коммуникация',
  'rh.frameworkAddFamily': 'Добавить',
  'rh.frameworkNewCompLabel': 'Новая компетенция',
  'rh.frameworkDomain': 'Домен',
  'rh.frameworkFamily': 'Семейство',
  'rh.frameworkCompName': 'Название',
  'rh.frameworkCompPlaceholder': 'Напр.: Синтез документов',
  'rh.frameworkEchelle': 'Шкала',
  'rh.frameworkEchelleHint': 'Уровни 1–5 (для сетки оценки)',
  'rh.frameworkAddCompetency': 'Добавить компетенцию',
  'rh.frameworkDomainNameHint': 'Название домена (мин. 2 символа).',
  'rh.frameworkDomainCreated': 'Домен добавлен.',
  'rh.frameworkPickDomainFirst': 'Сначала создайте домен.',
  'rh.frameworkFamilyNameHint': 'Название семейства (мин. 2 символа).',
  'rh.frameworkFamilyCreated': 'Семейство добавлено.',
  'rh.frameworkPickFamilyFirst': 'Выберите семейство или создайте в домене.',
  'rh.frameworkCompNameHint': 'Укажите название компетенции.',
  'rh.frameworkCompCreated': 'Компетенция добавлена.',
  'rh.frameworkDeleteSuccess': 'Элемент удалён из справочника.',
  'rh.frameworkDeleteDomain': 'Удалить этот домен',
  'rh.frameworkDeleteFamily': 'Удалить это семейство',
  'rh.frameworkDeleteCompetency': 'Удалить эту компетенцию',
  'rh.frameworkDeleteTitle': 'Удалить этот элемент?',
  'rh.frameworkDeleteMessage': 'Вы удалите «{{name}}». Это необратимо.',
  'rh.frameworkDeleteConfirm': 'Удалить',
  'rh.competencyFrameworkEmpty': 'Справочник для вашей организации ещё не настроен.',
  'rh.competencyFrameworkEmptyUserHint':
    'Начните с домена, затем добавьте семейства и компетенции в форме выше.',
  'rh.competencyFrameworkEmptyContactRh':
    'Обратитесь к HR или администратору LyrraStudio для настройки.',
  'rh.skillShort': 'компетенции',
  'rh.targetShort': 'цель',
  'rh.allDepartments': 'Все подразделения',
  'rh.allCompetencies': 'Все компетенции',
  'rh.competencyMap': 'Карта',
  'rh.competencyMapEmpty':
    'Нет подтверждённых оценок с подразделением — карта заполнится после оценок HR.',
  'rh.level': 'Уровень',
  'rh.department': 'Подразделение',
  'rh.noValidatedSample': 'Нет оценки',
  'rh.avgLevel': 'Ср.',
  'rh.departments': 'подразделения',
  'rh.avgOverall': 'Общий средний',
  'rh.totalGaps': 'Разрывы (отрицательные игнорируются)',
  'rh.criticalGaps': 'Критические (разрыв ≥ 2)',
  'rh.avgGap': 'Средний разрыв',
  'rh.gapAnalysis': 'Анализ разрывов',
  'rh.current': 'Текущий',
  'rh.required': 'Ожидаемый',
  'rh.noGapsFilter': 'Нет разрывов для этого фильтра.',
  'rh.noGapData': 'Разрывы не обнаружены (или нет подтверждённых оценок).',
  'rh.priority.high': 'Критический',
  'rh.priority.medium': 'Умеренный',
  'rh.priority.low': 'Низкий',
  'rh.gapRecommendationSuffix': ' Обучение или план развития в соответствии с ожидаемым уровнем.',
};

const ar: RhCompetenciesStrings = {
  ...en,
  'rh.competencies.title': 'إدارة الكفاءات',
  'rh.competencies.subtitle': 'المرجع، الخريطة الحرارية وتحليل الفجوات',
  'rh.competencies.documentTitle': 'الكفاءات - LyrraStudio',
  'rh.competencies.tabs.framework': 'المرجع',
  'rh.competencies.tabs.map': 'الخريطة',
  'rh.competencies.tabs.gap': 'تحليل الفجوات',
  'rh.competencyFrameworkDefaults': 'مرجع الكفاءات',
  'rh.competencies': 'كفاءات',
  'rh.searchCompetencies': 'البحث عن كفاءة…',
  'rh.frameworkLoadForbidden': 'لا يمكنك عرض هذا المرجع بهذا الحساب.',
  'rh.frameworkSession': 'انتهت الجلسة. سجّل الدخول مجدداً.',
  'rh.frameworkLoadError': 'تعذّر عرض المرجع حالياً. أعد المحاولة.',
  'rh.frameworkSaveForbidden': 'هذا الإجراء مخصّص لمسؤولي الموارد البشرية والمسؤولين.',
  'rh.frameworkDuplicate': 'هذا الاسم مستخدم بالفعل.',
  'rh.frameworkSaveError': 'فشل الحفظ. أعد المحاولة.',
  'rh.frameworkReadOnly': 'للقراءة فقط — التعديلات من الموارد البشرية أو المسؤولين.',
  'rh.frameworkBuildSection': 'بناء المرجع',
  'rh.frameworkNewDomainLabel': 'مجال جديد',
  'rh.frameworkNewDomainPlaceholder': 'مثال: المهارات المهنية',
  'rh.frameworkAddDomain': 'إضافة مجال',
  'rh.frameworkNewFamilyLabel': 'عائلة كفاءات جديدة',
  'rh.frameworkParentDomain': 'المجال الأب',
  'rh.frameworkFamilyName': 'اسم العائلة',
  'rh.frameworkFamilyPlaceholder': 'مثال: التواصل',
  'rh.frameworkAddFamily': 'إضافة',
  'rh.frameworkNewCompLabel': 'كفاءة جديدة',
  'rh.frameworkDomain': 'مجال',
  'rh.frameworkFamily': 'عائلة',
  'rh.frameworkCompName': 'الاسم',
  'rh.frameworkCompPlaceholder': 'مثال: تلخيص المستندات',
  'rh.frameworkEchelle': 'المقياس',
  'rh.frameworkEchelleHint': 'المستويات 1 إلى 5',
  'rh.frameworkAddCompetency': 'إضافة كفاءة',
  'rh.frameworkDomainNameHint': 'اسم المجال (حرفان على الأقل).',
  'rh.frameworkDomainCreated': 'تمت إضافة المجال.',
  'rh.frameworkPickDomainFirst': 'أنشئ مجالاً أولاً.',
  'rh.frameworkFamilyNameHint': 'اسم العائلة (حرفان على الأقل).',
  'rh.frameworkFamilyCreated': 'تمت إضافة العائلة.',
  'rh.frameworkPickFamilyFirst': 'اختر عائلة أو أنشئ واحدة في مجال.',
  'rh.frameworkCompNameHint': 'أدخل اسم الكفاءة.',
  'rh.frameworkCompCreated': 'تمت إضافة الكفاءة.',
  'rh.frameworkDeleteSuccess': 'تم حذف العنصر من المرجع.',
  'rh.frameworkDeleteDomain': 'حذف هذا المجال',
  'rh.frameworkDeleteFamily': 'حذف هذه العائلة',
  'rh.frameworkDeleteCompetency': 'حذف هذه الكفاءة',
  'rh.frameworkDeleteTitle': 'حذف هذا العنصر؟',
  'rh.frameworkDeleteMessage': 'ستحذف «{{name}}». هذا نهائي.',
  'rh.frameworkDeleteConfirm': 'حذف',
  'rh.competencyFrameworkEmpty': 'لا يوجد مرجع بعد لمؤسستكم.',
  'rh.competencyFrameworkEmptyUserHint':
    'ابدأ بإضافة مجال، ثم عائلات وكفاءات عبر النموذج أعلاه.',
  'rh.competencyFrameworkEmptyContactRh':
    'اطلب من مسؤولي الموارد البشرية أو LyrraStudio إعداد المرجع.',
  'rh.skillShort': 'كفاءات',
  'rh.targetShort': 'هدف',
  'rh.allDepartments': 'جميع الأقسام',
  'rh.allCompetencies': 'جميع الكفاءات',
  'rh.competencyMap': 'الخريطة الحرارية',
  'rh.competencyMapEmpty':
    'لا توجد تقييمات مع قسم — ستُملأ الخريطة بعد تقييمات الموارد البشرية.',
  'rh.level': 'المستوى',
  'rh.department': 'القسم',
  'rh.noValidatedSample': 'لا توجد درجة',
  'rh.avgLevel': 'متوسط',
  'rh.departments': 'أقسام',
  'rh.avgOverall': 'المتوسط العام',
  'rh.totalGaps': 'الفجوات (السلبية متجاهلة)',
  'rh.criticalGaps': 'حرجة (فجوة ≥ 2)',
  'rh.avgGap': 'متوسط الفجوة',
  'rh.gapAnalysis': 'تحليل الفجوات',
  'rh.current': 'الحالي',
  'rh.required': 'المطلوب',
  'rh.noGapsFilter': 'لا فجوات لهذا الفلتر.',
  'rh.noGapData': 'لا فجوات (أو لا تقييمات معتمدة بعد).',
  'rh.priority.high': 'حرج',
  'rh.priority.medium': 'متوسط',
  'rh.priority.low': 'منخفض',
  'rh.gapRecommendationSuffix': ' تدريب أو خطة تطوير بما يتوافق مع المستوى المطلوب.',
};

const tr: RhCompetenciesStrings = {
  ...en,
  'rh.competencies.title': 'Yetkinlik yönetimi',
  'rh.competencies.subtitle': 'Çerçeve, ısı haritası ve boşluk analizi',
  'rh.competencies.documentTitle': 'Yetkinlikler - LyrraStudio',
  'rh.competencies.tabs.framework': 'Çerçeve',
  'rh.competencies.tabs.map': 'Harita',
  'rh.competencies.tabs.gap': 'Boşluk analizi',
  'rh.competencyFrameworkDefaults': 'Yetkinlik çerçevesi',
  'rh.competencies': 'yetkinlik',
  'rh.searchCompetencies': 'Yetkinlik ara…',
  'rh.frameworkLoadForbidden': 'Bu hesapla çerçeveyi görüntüleyemezsiniz.',
  'rh.frameworkSession': 'Oturum süresi doldu. Yeniden giriş yapın.',
  'rh.frameworkLoadError': 'Çerçeve şu an gösterilemiyor. Tekrar deneyin.',
  'rh.frameworkSaveForbidden': 'Bu işlem İK sorumluları ve yöneticilere ayrılmıştır.',
  'rh.frameworkDuplicate': 'Bu ad zaten kullanılıyor.',
  'rh.frameworkSaveError': 'Kaydedilemedi. Tekrar deneyin.',
  'rh.frameworkReadOnly': 'Salt okunur — değişiklikler İK veya yöneticiler tarafından yapılır.',
  'rh.frameworkBuildSection': 'Çerçeveyi oluştur',
  'rh.frameworkNewDomainLabel': 'Yeni alan',
  'rh.frameworkNewDomainPlaceholder': 'Örn. Mesleki beceriler',
  'rh.frameworkAddDomain': 'Alan ekle',
  'rh.frameworkNewFamilyLabel': 'Yeni yetkinlik ailesi',
  'rh.frameworkParentDomain': 'Üst alan',
  'rh.frameworkFamilyName': 'Aile adı',
  'rh.frameworkFamilyPlaceholder': 'Örn. İletişim',
  'rh.frameworkAddFamily': 'Ekle',
  'rh.frameworkNewCompLabel': 'Yeni yetkinlik',
  'rh.frameworkDomain': 'Alan',
  'rh.frameworkFamily': 'Aile',
  'rh.frameworkCompName': 'Ad',
  'rh.frameworkCompPlaceholder': 'Örn. Belge özeti',
  'rh.frameworkEchelle': 'Ölçek',
  'rh.frameworkEchelleHint': '1–5 düzeyleri (değerlendirme ızgarası)',
  'rh.frameworkAddCompetency': 'Yetkinlik ekle',
  'rh.frameworkDomainNameHint': 'Alan adı (en az 2 karakter).',
  'rh.frameworkDomainCreated': 'Alan eklendi.',
  'rh.frameworkPickDomainFirst': 'Önce bir alan oluşturun.',
  'rh.frameworkFamilyNameHint': 'Aile adı (en az 2 karakter).',
  'rh.frameworkFamilyCreated': 'Aile eklendi.',
  'rh.frameworkPickFamilyFirst': 'Bir aile seçin veya bir alanda oluşturun.',
  'rh.frameworkCompNameHint': 'Yetkinlik adını girin.',
  'rh.frameworkCompCreated': 'Yetkinlik eklendi.',
  'rh.frameworkDeleteSuccess': 'Öğe çerçeveden kaldırıldı.',
  'rh.frameworkDeleteDomain': 'Bu alanı sil',
  'rh.frameworkDeleteFamily': 'Bu aileyi sil',
  'rh.frameworkDeleteCompetency': 'Bu yetkinliği sil',
  'rh.frameworkDeleteTitle': 'Bu öğe silinsin mi?',
  'rh.frameworkDeleteMessage': '«{{name}}» silinecek. Bu geri alınamaz.',
  'rh.frameworkDeleteConfirm': 'Sil',
  'rh.competencyFrameworkEmpty': 'Kuruluşunuz için henüz çerçeve yok.',
  'rh.competencyFrameworkEmptyUserHint':
    'Üstteki form ile bir alan, ardından aileler ve yetkinlikler ekleyin.',
  'rh.competencyFrameworkEmptyContactRh':
    'Çerçevenin kurulumu için İK veya LyrraStudio yöneticinize başvurun.',
  'rh.skillShort': 'yetkinlik',
  'rh.targetShort': 'hedef',
  'rh.allDepartments': 'Tüm birimler',
  'rh.allCompetencies': 'Tüm yetkinlikler',
  'rh.competencyMap': 'Isı haritası',
  'rh.competencyMapEmpty':
    'Birimli onaylı değerlendirme yok — harita İK değerlendirmelerinden sonra dolacak.',
  'rh.level': 'Düzey',
  'rh.department': 'Birim',
  'rh.noValidatedSample': 'Not yok',
  'rh.avgLevel': 'Ort.',
  'rh.departments': 'birim',
  'rh.avgOverall': 'Genel ortalama',
  'rh.totalGaps': 'Boşluklar (negatifler yok sayılır)',
  'rh.criticalGaps': 'Kritik (boşluk ≥ 2)',
  'rh.avgGap': 'Ortalama boşluk',
  'rh.gapAnalysis': 'Boşluk analizi',
  'rh.current': 'Mevcut',
  'rh.required': 'Beklenen',
  'rh.noGapsFilter': 'Bu filtre için boşluk yok.',
  'rh.noGapData': 'Boşluk yok (veya henüz onaylı değerlendirme yok).',
  'rh.priority.high': 'Kritik',
  'rh.priority.medium': 'Orta',
  'rh.priority.low': 'Düşük',
  'rh.gapRecommendationSuffix': ' Beklenen düzeye uygun eğitim veya gelişim planı.',
};

export const institutionRhCompetenciesStrings = {
  fr,
  en,
  es,
  de,
  it,
  pt,
  lb,
  uk,
  ru,
  ar,
  tr,
} as const;
